// ─── src/aiClient.js ──────────────────────────────────────────────────────────
// Single entry point for all AI calls.
// The proxy at 127.0.0.1:3001/api/ai handles the waterfall:
//   Anthropic claude-sonnet-4-6 → Gemini 2.0 Flash → Groq llama-3.3-70b
//
// Usage:
//   import { callAI } from "./aiClient";
//   const text = await callAI({ system: "...", messages: [...], maxTokens: 1000 });

// Use relative URL to call local /api/ai endpoint (avoids CORS issues with external proxy)
// In dev: routes to localhost API
// In production: routes to Vercel /api/ai
const PROXY_SECRET = import.meta.env.VITE_PROXY_SECRET || "";

export async function callAI({ system, messages, maxTokens = 1500 }) {
  const headers = { "Content-Type": "application/json" };
  if (PROXY_SECRET) headers["x-proxy-secret"] = PROXY_SECRET;

  const res = await fetch(`/api/ai`, {
    method: "POST",
    headers,
    body: JSON.stringify({ system, messages, max_tokens: maxTokens }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `AI proxy error ${res.status}`);
  }

  const data = await res.json();
  // Proxy always returns { content: [{ type: "text", text: "..." }], provider: "..." }
  const text = data.content?.map(c => c.text || "").join("") || "";
  if (!text) throw new Error("Empty response from AI");
  return text;
}

// ── Ollama (local only — bypasses proxy since it's on localhost anyway) ────────
export async function callOllama({ system, messages, maxTokens = 1500 }) {
  const baseUrl = import.meta.env.VITE_OLLAMA_URL || "http://localhost:11434";
  const model   = import.meta.env.VITE_OLLAMA_MODEL || "llama3";

  const ollamaMessages = system
    ? [{ role: "system", content: system }, ...messages]
    : messages;

  const res = await fetch(`${baseUrl}/api/chat`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model, messages: ollamaMessages, stream: false }),
  });

  if (!res.ok) throw new Error(`Ollama error ${res.status}`);
  const data = await res.json();
  return data.message?.content || "";
}

// ── getConfiguredProviders — for UI display only ──────────────────────────────
export function getConfiguredProviders() {
  // We can't check server-side keys from the browser.
  // The proxy will try each in order and skip ones that aren't configured.
  return ["Anthropic", "Gemini", "Groq"];
}
